<template>
    <div class="booth-info-container" :class="{ 'booth-busy': waitingCount > 5 }">
        <!-- Background Decoration -->
        <div class="background-decoration">
            <div class="decoration-blob blob-1"></div>
            <div class="decoration-blob blob-2"></div>
            <div class="decoration-blob blob-3"></div>
        </div>

        <!-- Header Section -->
        <header class="booth-info-header">
            <div class="container-fluid h-100">
                <div class="row h-100 align-items-center">
                    <div class="col-12">
                        <div class="header-content">                            
                            <div class="header-info">
                                <h1 class="service-name">{{ serviceName }}</h1>
                                <div class="officer-info-badge">
                                    <span class="badge-officer">👤 Cán bộ: {{ currentOfficerName }}</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </header>

        <!-- Main Content -->
        <main class="booth-info-main">
            <div class="container-fluid h-100">
                <div class="row h-100 align-items-center">
                    <!-- Left Section: Officer Photo -->
                    <div class="col-md-4 col-lg-4 col-xl-3 mb-4 mb-md-0">
                        <div class="officer-photo-wrapper">
                            <div class="photo-container" :class="{ 'photo-active': isConnected }">
                                <img 
                                    v-if="avatar"
                                    :src="baseUrl + avatar" 
                                    :alt="currentOfficerName"
                                    class="officer-photo"
                                />
                                <img 
                                    v-else
                                    :src="require(`@/assets/default-avatar.jpg`)" 
                                    :alt="currentOfficerName"
                                    class="officer-photo"
                                />
                                <div class="photo-overlay">
                                    <User :size="72" class="text-white opacity-70" />
                                </div>
                                <div class="photo-status-indicator"></div>
                            </div>
                            <div class="officer-status-badge" :class="statusClass">
                                {{ statusMessage }}
                            </div>
                        </div>
                    </div>

                    <!-- Right Section: Booth Info -->
                    <div class="col-md-8 col-lg-8 col-xl-9" style="height: 100%;">
                        <div class="booth-info-card" style="height: 100%;">
                            <!-- Booth Name & Number -->
                            <div class="booth-header-section">
                                <div class="booth-number-badge">{{ boothName }}</div>
                                <div class="booth-status-info">
                                    <span class="working-hours">
                                        <Clock :size="18" class="icon" />
                                        {{ workingHours }}
                                    </span>
                                </div>
                            </div>

                            <!-- Queue Number Display - Main Attraction -->
                            <div class="queue-number-section">
                                <div class="queue-label">Số thứ tự</div>
                                <div class="queue-number-display-wrapper">
                                    <div class="queue-animation-wrapper">
                                        <div class="queue-number-display">{{ currentQueueNumber }}</div>
                                    </div>
                                </div>
                                <div class="queue-subtitle">Vui lòng đến quầy</div>
                            </div>

                            <!-- Queue Statistics -->
                            <!-- <div class="queue-stats-section">
                                <div class="stat-item stat-waiting">
                                    <div class="stat-icon">
                                        <Ticket :size="20" />
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-label">Khách chờ</div>
                                        <div class="stat-value">{{ waitingCount }}</div>
                                    </div>
                                </div>
                                <div class="stat-divider"></div>
                                <div class="stat-item stat-total">
                                    <div class="stat-icon">
                                        <CheckCircle :size="20" />
                                    </div>
                                    <div class="stat-content">
                                        <div class="stat-label">Hôm nay</div>
                                        <div class="stat-value">{{ todayTotal }}</div>
                                    </div>
                                </div>
                            </div> -->
                        </div>
                    </div>
                </div>
            </div>
        </main>
    </div>
</template>

<script>
import { 
    Building, 
    Building2, 
    User, 
    CheckCircle, 
    Ticket,
    Clock,
    AlertCircle
} from 'lucide-vue-next';
import { Actions, Employee } from "@/store/enums/StoreEnums";
import { useStore } from "vuex";
import { useRouter } from "vue-router";
import FingerprintJS from '@fingerprintjs/fingerprintjs'
import { subscribe, unsubscribe } from '@/websocket/stompService';
import { baseUrl } from '@/utils/global';
import JwtService from '@/core/services/JwtService';
import ApiService from '@/core/services/ApiService';

// const SIX_HOURS_IN_MS = 3 * 60 * 60 * 1000;
const SIX_HOURS_IN_MS = 10 * 1000;
let loginInterval = null;

export default {
    name: 'BoothInfo',
    components: {
        Building,
        Building2,
        User,
        CheckCircle,
        Ticket,
        Clock,
        AlertCircle
    },
    data() {
        return {
            router: useRouter(),
            store: useStore(),
            serviceName: "",
            currentOfficerName: "",
            officerPhotoUrl: null,
            boothName: "",
            currentQueueNumber: "----",
            waitingCount: 6,
            todayTotal: 156,
            workingHours: "07:30 - 17:00",
            isWarning: false,
            statusMessage: "Đang phục vụ khách hàng",
            isOnline: true,
            animationKey: 0,
            userName: "",
            avatar: "",
            baseUrl,
        }
    },
    computed: {
        isConnected() {
            return this.currentQueueNumber && this.boothName;
        },
        statusClass() {
            if (this.waitingCount > 5) return 'status-busy';
            if (this.waitingCount > 0) return 'status-moderate';
            return 'status-available';
        }
    },
    methods: {
        async loadBoothData() {
            const device_id = await localStorage.getItem("booth-info-device-id")
            // const fp = await FingerprintJS.load()
            // const result = await fp.get()
            // const visitor_id = result.visitorId

            if(!device_id) {
                this.router.push("boothLogin")
            }
            else {
                const data = await this.store.dispatch(Employee.findByDeviceId, device_id)
                if(data.code == 200 && data.data) {
                    this.serviceName = data.data.cTenDichVu
                    this.currentOfficerName = data.data.cTenNhanVien
                    this.boothName = data.data.cTenQuay
                    this.userName = data.data.cTenTaiKhoan
                    this.avatar = data.data.cHinhAnh

                    subscribe("/topic/" + this.userName + "/process-number", this.loadCurrentQueueNumber);
                }
                else {
                    this.router.push("boothLogin")
                    console.log("Error: " + data.msg)
                }
            }
        },
        
        loadCurrentQueueNumber(data) {
            const result = JSON.parse(data.body);
            if(result.code == 200 && result.data) {
                this.currentQueueNumber = result.data.cSoThuTu;
            }
            else {
                this.currentQueueNumber = "----";
            }
        },

        onQueueNumberChange() {
            this.animationKey++;
        },

        async reLogin() {
            try {
            const result = await this.store.dispatch(Actions.RE_LOGIN, {});      
            if (result.code == 200 && result.data) {
                await JwtService.saveToken(result.data);
                await ApiService.setHeader();
            }
            } catch (error) {
            console.error('Lỗi khi tự động đăng nhập lại:', error)
            }
        }
    },
    watch: {
        currentQueueNumber() {
            this.onQueueNumberChange();
        }
    },
    mounted() {
        this.loadBoothData();
        loginInterval = setInterval(this.reLogin, SIX_HOURS_IN_MS)
    },
    beforeUnmount() {
        unsubscribe("/topic/" + this.userName + "/process-number", this.loadCurrentQueueNumber);
        if (loginInterval) {
            clearInterval(loginInterval)
        }
    }
};
</script>

<style lang="scss" scoped>
// ============ COLOR VARIABLES ============
$primary-gradient: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
$accent-gradient: linear-gradient(135deg, #f093fb 0%, #f5576c 100%);
$success-color: #10b981;
$warning-color: #f59e0b;
$danger-color: #ef4444;
$neutral-bg: #f8fafc;
$card-shadow: 0 20px 60px rgba(0, 0, 0, 0.08);
$card-shadow-hover: 0 30px 90px rgba(102, 126, 234, 0.15);

// ============ GLOBAL SETUP ============
* {
    box-sizing: border-box;
}

.booth-info-container {
    display: flex;
    flex-direction: column;
    min-height: 100vh;
    background: linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%);
    position: relative;
    overflow: hidden;
    transition: all 0.6s cubic-bezier(0.4, 0, 0.2, 1);
    
    &.booth-busy {
        background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
    }
}

// ============ BACKGROUND DECORATION ============
.background-decoration {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    pointer-events: none;
    z-index: 0;
    
    .decoration-blob {
        position: absolute;
        border-radius: 50%;
        opacity: 0.06;
        animation: float-blob 8s ease-in-out infinite;
    }
    
    .blob-1 {
        width: 400px;
        height: 400px;
        background: #667eea;
        top: -10%;
        right: -5%;
        animation-delay: 0s;
    }
    
    .blob-2 {
        width: 300px;
        height: 300px;
        background: #f5576c;
        bottom: 10%;
        left: -10%;
        animation-delay: 2s;
    }
    
    .blob-3 {
        width: 350px;
        height: 350px;
        background: #764ba2;
        top: 50%;
        left: 50%;
        animation-delay: 4s;
    }
}

@keyframes float-blob {
    0%, 100% { transform: translate(0, 0) scale(1); }
    33% { transform: translate(30px, -50px) scale(1.1); }
    66% { transform: translate(-20px, 30px) scale(0.9); }
}

// ============ HEADER SECTION ============
.booth-info-header {
    background: $primary-gradient;
    color: white;
    padding: 2.5rem 0;
    box-shadow: 0 15px 50px rgba(102, 126, 234, 0.2);
    position: relative;
    z-index: 10;
    
    &::after {
        content: '';
        position: absolute;
        bottom: -1px;
        left: 0;
        right: 0;
        height: 10px;
        background: linear-gradient(to right, transparent, rgba(255,255,255,0.1) 50%, transparent);
        filter: blur(5px);
    }
    
    .header-content {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 2rem;
        text-align: center;
        
        .header-badge {
            .badge-live {
                display: inline-block;
                background: rgba(255, 255, 255, 0.25);
                color: white;
                padding: 0.4rem 0.95rem;
                border-radius: 50px;
                font-size: 0.75rem;
                font-weight: 700;
                letter-spacing: 1px;
                text-transform: uppercase;
                backdrop-filter: blur(10px);
                border: 1px solid rgba(255, 255, 255, 0.3);
                animation: pulse-badge 2s ease-in-out infinite;
            }
        }
        
        .header-info {
            flex: 1;
            
            .service-label {
                margin: 0;
                font-size: 0.9rem;
                opacity: 0.95;
                font-weight: 500;
                letter-spacing: 1px;
                text-transform: uppercase;
                margin-bottom: 0.5rem;
            }
            
            .service-name {
                color: #fff;
                font-size: clamp(2rem, 5vw, 4rem);
                font-weight: 800;
                margin: 0.5rem 0;
                letter-spacing: 0.8px;
                text-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
                text-transform: uppercase;
            }
            
            .officer-info-badge {
                display: inline-block;
                margin-top: 1rem;
                
                .badge-officer {
                    text-transform: uppercase;
                    display: inline-block;
                    background: rgba(255, 255, 255, 0.15);
                    color: white;
                    padding: 0.6rem 1.5rem;
                    border-radius: 50px;
                    font-size: clamp(1rem, 3vw, 3rem);
                    font-weight: 600;
                    backdrop-filter: blur(10px);
                    border: 1px solid rgba(255, 255, 255, 0.2);
                    transition: all 0.3s ease;
                    
                    &:hover {
                        background: rgba(255, 255, 255, 0.25);
                        transform: translateY(-2px);
                    }
                }
            }
        }
    }
}

@keyframes pulse-badge {
    0%, 100% {
        box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.4);
    }
    50% {
        box-shadow: 0 0 0 6px rgba(255, 255, 255, 0);
    }
}

// ============ MAIN CONTENT ============
.booth-info-main {
    flex: 1;
    display: flex;
    align-items: center;
    padding: 2rem 0;
    position: relative;
    z-index: 5;
    
    @media (max-width: 768px) {
        padding: 2rem 0;
        align-items: flex-start;
    }
}

// ============ OFFICER PHOTO SECTION ============
.officer-photo-wrapper {
    text-align: center;
    
    .photo-container {
        position: relative;
        width: 100%;
        margin: 0 auto 2rem;
        border-radius: 24px;
        overflow: hidden;
        aspect-ratio: 0.8;
        background: linear-gradient(135deg, #e9ecef 0%, #f0f0f0 100%);
        box-shadow: $card-shadow;
        transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
        
        &.photo-active {
            box-shadow: 0 20px 60px rgba(102, 126, 234, 0.25);
            transform: scale(1.02);
            
            .photo-status-indicator {
                animation: pulse-indicator 2s ease-in-out infinite;
            }
        }
        
        img {
            width: 100%;
            height: 100%;
            object-fit: cover;
            display: block;
            transition: transform 0.6s ease;
        }
        
        &:hover img {
            transform: scale(1.08);
        }
        
        .photo-overlay {
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(102, 126, 234, 0.1);
            backdrop-filter: blur(4px);
            opacity: 0;
            transition: opacity 0.4s ease;
        }
        
        &:hover .photo-overlay {
            opacity: 1;
        }
        
        .photo-status-indicator {
            position: absolute;
            bottom: 15px;
            right: 15px;
            width: 18px;
            height: 18px;
            background: $success-color;
            border: 4px solid white;
            border-radius: 50%;
            box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4);
        }
    }
    
    .officer-status-badge {
        display: inline-block;
        padding: 0.8rem 1.8rem;
        border-radius: 50px;
        font-weight: 600;
        font-size: 0.95rem;
        text-transform: uppercase;
        letter-spacing: 0.5px;
        transition: all 0.3s ease;
        border: 2px solid transparent;
        
        &.status-available {
            background: linear-gradient(135deg, #b5f5e8 0%, #a8edea 100%);
            color: #047857;
            border-color: #6ee7d7;
        }
        
        &.status-moderate {
            background: linear-gradient(135deg, #fef08a 0%, #fde047 100%);
            color: #92400e;
            border-color: #fcd34d;
        }
        
        &.status-busy {
            background: linear-gradient(135deg, #fecaca 0%, #fca5a5 100%);
            color: #7f1d1d;
            border-color: #f87171;
            animation: pulse-busy 1.5s ease-in-out infinite;
        }
    }
}

@keyframes pulse-indicator {
    0%, 100% {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4), 0 0 0 0 rgba(16, 185, 129, 0.3);
    }
    50% {
        box-shadow: 0 4px 12px rgba(16, 185, 129, 0.4), 0 0 0 8px rgba(16, 185, 129, 0);
    }
}

@keyframes pulse-busy {
    0%, 100% { transform: scale(1); }
    50% { transform: scale(1.05); }
}

// ============ BOOTH INFO CARD ============
.booth-info-card {
    background: white;
    border-radius: 24px;
    padding: 3.5rem;
    box-shadow: $card-shadow;
    border: 1px solid rgba(255, 255, 255, 0.8);
    transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
    position: relative;
    overflow: hidden;
    
    &::before {
        content: '';
        position: absolute;
        top: -50%;
        right: -50%;
        width: 300px;
        height: 300px;
        background: radial-gradient(circle, rgba(102, 126, 234, 0.05) 0%, transparent 70%);
        border-radius: 50%;
        pointer-events: none;
    }
    
    &:hover {
        box-shadow: $card-shadow-hover;
        transform: translateY(-4px);
        border-color: rgba(102, 126, 234, 0.15);
    }
    
    @media (max-width: 768px) {
        padding: 2rem;
    }
    
    @media (max-width: 576px) {
        padding: 1.5rem;
        border-radius: 16px;
    }
}

// ============ BOOTH HEADER SECTION ============
.booth-header-section {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 2rem;
    margin-bottom: 3rem;
    padding-bottom: 2rem;
    border-bottom: 2px solid #f0f0f0;
    
    @media (max-width: 768px) {
        flex-wrap: wrap;
        margin-bottom: 2rem;
    }
    
    .booth-number-badge {
        display: inline-flex;
        align-items: center;
        background: $primary-gradient;
        color: white;
        padding: 0.9rem 2rem;
        border-radius: 50px;
        font-size: 2rem;
        font-weight: 700;
        letter-spacing: 1px;
        text-transform: uppercase;
        box-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
        transition: all 0.3s ease;
        
        &:hover {
            transform: translateY(-2px);
            box-shadow: 0 12px 32px rgba(102, 126, 234, 0.4);
        }
    }
    
    .booth-status-info {
        display: flex;
        align-items: center;
        gap: 1.5rem;
        
        .working-hours {
            display: flex;
            align-items: center;
            gap: 0.6rem;
            color: #666;
            font-weight: 600;
            font-size: 1.55rem;
            
            .icon {
                color: $primary-gradient;
            }
        }
    }
}

// ============ QUEUE NUMBER SECTION - MAIN FOCUS ============
.queue-number-section {
    text-align: center;
    margin: 2.5rem 0;
    position: relative;
    
    .queue-label {
        font-size: 1.25rem;
        color: #999;
        font-weight: 700;
        text-transform: uppercase;
        letter-spacing: 1.5px;
        margin-bottom: 1.5rem;
        display: block;
    }
    
    .queue-number-display-wrapper {
        display: flex;
        align-items: center;
        justify-content: center;        
        
        .queue-animation-wrapper {
            position: relative;
            
            .queue-number-display {
                font-size: clamp(5rem, 18vw, 14rem);
                font-weight: 900;
                color: white;
                background: $primary-gradient;
                padding: 1.5rem 3rem;
                border-radius: 20px;
                text-shadow: 0 8px 24px rgba(102, 126, 234, 0.3);
                box-shadow: 0 25px 60px rgba(102, 126, 234, 0.25), 
                            inset 0 1px 0 rgba(255, 255, 255, 0.2);
                letter-spacing: 2px;
                min-width: 200px;
                text-align: center;
                animation: scaleIn 0.6s cubic-bezier(0.34, 1.56, 0.64, 1);
                line-height: 1.1;
                transition: all 0.4s ease;
                
                @media (max-width: 768px) {
                    font-size: 4rem;
                    padding: 1rem 2rem;
                    min-width: 140px;
                }
                
                @media (max-width: 576px) {
                    font-size: 3rem;
                    padding: 0.8rem 1.5rem;
                }
            }
        }
    }
    
    .queue-subtitle {
        font-size: 1.25rem;
        color: #666;
        font-weight: 600;
        margin-top: 1.5rem;
        letter-spacing: 0.5px;
    }
}

@keyframes scaleIn {
    0% {
        transform: scale(0.7) rotateZ(-5deg);
        opacity: 0;
    }
    50% {
        transform: scale(1.05) rotateZ(1deg);
    }
    100% {
        transform: scale(1) rotateZ(0deg);
        opacity: 1;
    }
}

// ============ QUEUE STATISTICS SECTION ============
.queue-stats-section {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 2rem;
    margin-top: 3rem;
    padding-top: 2rem;
    border-top: 2px solid #f0f0f0;
    
    @media (max-width: 576px) {
        flex-direction: column;
        gap: 1.5rem;
    }
    
    .stat-item {
        display: flex;
        align-items: center;
        gap: 1.2rem;
        padding: 1rem;
        border-radius: 14px;
        transition: all 0.3s ease;
        flex: 1;
        max-width: 180px;
        background: #f8fafc;
        
        &.stat-waiting {
            border: 2px solid rgba(102, 126, 234, 0.1);
            
            .stat-icon {
                color: #667eea;
            }
            
            &:hover {
                background: linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(102, 126, 234, 0.02) 100%);
                border-color: #667eea;
            }
        }
        
        &.stat-total {
            border: 2px solid rgba(16, 185, 129, 0.1);
            
            .stat-icon {
                color: $success-color;
            }
            
            &:hover {
                background: linear-gradient(135deg, rgba(16, 185, 129, 0.05) 0%, rgba(16, 185, 129, 0.02) 100%);
                border-color: $success-color;
            }
        }
        
        .stat-icon {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 48px;
            height: 48px;
            border-radius: 50%;
            background: white;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.06);
            transition: transform 0.3s ease;
            flex-shrink: 0;
        }
        
        &:hover .stat-icon {
            transform: scale(1.1) rotate(5deg);
        }
        
        .stat-content {
            flex: 1;
            
            .stat-label {
                font-size: 0.8rem;
                color: #999;
                font-weight: 600;
                text-transform: uppercase;
                letter-spacing: 0.5px;
                margin-bottom: 0.4rem;
            }
            
            .stat-value {
                font-size: 1.8rem;
                font-weight: 800;
                background: $primary-gradient;
                -webkit-background-clip: text;
                -webkit-text-fill-color: transparent;
                background-clip: text;
            }
        }
    }
    
    .stat-divider {
        width: 2px;
        height: 60px;
        background: linear-gradient(180deg, transparent 0%, #e9ecef 50%, transparent 100%);
        
        @media (max-width: 576px) {
            display: none;
        }
    }
}

// ============ RESPONSIVE ============
@media (max-width: 768px) {
    .booth-info-container {
        &.booth-busy {
            background: linear-gradient(135deg, #ffecd2 0%, #fcb69f 100%);
        }
    }
    
    .booth-info-header {
        padding: 2rem 0;
        
        .header-content {
            gap: 1.5rem;
            text-align: center;
            
            .header-info {
                .service-label {
                    font-size: 0.8rem;
                }
                
                .service-name {
                    font-size: 1.8rem;
                }
                
                .officer-info-badge {
                    margin-top: 0.75rem;
                    
                    .badge-officer {
                        font-size: 0.9rem;
                        padding: 0.5rem 1.2rem;
                    }
                }
            }
        }
    }
    
    .booth-info-main {
        padding: 1.5rem 0;
    }
}

@media (max-width: 576px) {
    .booth-info-container {
        min-height: auto;
    }
    
    .booth-info-header {
        padding: 1.5rem 0;
        
        .header-content {
            flex-direction: column;
            gap: 1rem;
            
            .header-info {
                .service-name {
                    font-size: 1.4rem;
                }
                
                .officer-info-badge {
                    .badge-officer {
                        font-size: 0.8rem;
                    }
                }
            }
        }
    }
    
    .background-decoration {
        display: none;
    }
}

// ============ ACCESSIBILITY ============
@media (prefers-reduced-motion: reduce) {
    * {
        animation-duration: 0.01ms !important;
        animation-iteration-count: 1 !important;
        transition-duration: 0.01ms !important;
    }
}

// ============ DARK MODE SUPPORT ============
@media (prefers-color-scheme: dark) {
    .booth-info-container {
        background: linear-gradient(135deg, #1a202c 0%, #2d3748 100%);
        
        &.booth-busy {
            background: linear-gradient(135deg, #663c00 0%, #7d4a00 100%);
        }
    }
    
    .booth-info-card {
        background: #2d3748;
        border-color: rgba(255, 255, 255, 0.1);
        
        &:hover {
            border-color: rgba(102, 126, 234, 0.3);
        }
    }
    
    .booth-header-section {
        border-bottom-color: #4a5568;
    }
    
    .queue-stats-section {
        border-top-color: #4a5568;
        
        .stat-item {
            background: #4a5568;
            
            &.stat-waiting {
                border-color: rgba(102, 126, 234, 0.3);
            }
            
            &.stat-total {
                border-color: rgba(16, 185, 129, 0.3);
            }
            
            .stat-icon {
                background: #2d3748;
            }
        }
    }
    
    .queue-label,
    .queue-subtitle {
        color: #cbd5e0;
    }
    
    .booth-number-badge {
        color: white;
    }
}
</style>